import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API } from '@/utils/constants';
import { HelpTooltip } from '@/components/HelpTooltip';

/**
 * Reminders Page Component
 * Displays all follow-up reminders for jobs AND general reminders
 */
const RemindersPage = () => {
  const navigate = useNavigate();
  const [jobReminders, setJobReminders] = useState([]);
  const [generalReminders, setGeneralReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [newReminder, setNewReminder] = useState({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    priority: 'Normal'
  });

  useEffect(() => {
    fetchAllReminders();
  }, []);

  const fetchAllReminders = async () => {
    try {
      setLoading(true);
      
      // Fetch job reminders
      const jobsResponse = await axios.get(`${API}/jobs`);
      const jobsWithReminders = jobsResponse.data.filter(job => 
        !job.closed && job.comments && job.comments.some(c => c.follow_up_date && !c.completed)
      );
      
      const jobRemindersList = [];
      jobsWithReminders.forEach(job => {
        job.comments.forEach((comment, index) => {
          if (comment.follow_up_date && !comment.completed) {
            const followUpDate = new Date(comment.follow_up_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const daysOverdue = Math.floor((today - followUpDate) / (1000 * 60 * 60 * 24));
            
            jobRemindersList.push({
              type: 'job',
              jobId: job.id,
              jobNumber: job.job_number,
              customerName: job.customer_name,
              commentText: comment.text,
              followUpDate: comment.follow_up_date,
              commentIndex: index,
              daysOverdue: daysOverdue
            });
          }
        });
      });
      
      setJobReminders(jobRemindersList);

      // Fetch general reminders
      const generalResponse = await axios.get(`${API}/reminders`);
      const generalList = generalResponse.data.map(r => {
        const followUpDate = new Date(r.due_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const daysOverdue = Math.floor((today - followUpDate) / (1000 * 60 * 60 * 24));
        
        return {
          type: 'general',
          ...r,
          followUpDate: r.due_date,
          daysOverdue: daysOverdue
        };
      });
      
      setGeneralReminders(generalList);
    } catch (error) {
      console.error('Error fetching reminders:', error);
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteJobReminder = async (jobId, commentIndex) => {
    try {
      await axios.put(`${API}/jobs/${jobId}/comments/${commentIndex}/complete`);
      toast.success('Reminder marked as complete!');
      fetchAllReminders();
    } catch (error) {
      console.error('Error completing reminder:', error);
      toast.error('Failed to complete reminder');
    }
  };

  const handleCompleteGeneralReminder = async (reminderId) => {
    try {
      await axios.put(`${API}/reminders/${reminderId}/complete`);
      toast.success('Reminder marked as complete!');
      fetchAllReminders();
    } catch (error) {
      console.error('Error completing reminder:', error);
      toast.error('Failed to complete reminder');
    }
  };

  const handleDeleteGeneralReminder = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) return;
    
    try {
      await axios.delete(`${API}/reminders/${reminderId}`);
      toast.success('Reminder deleted!');
      fetchAllReminders();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error('Failed to delete reminder');
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.title.trim()) {
      toast.error('Please enter a task name');
      return;
    }
    if (!newReminder.due_date) {
      toast.error('Please select a due date');
      return;
    }

    try {
      await axios.post(`${API}/reminders`, newReminder);
      toast.success('Reminder created!');
      setShowReminderModal(false);
      setNewReminder({
        title: '',
        description: '',
        due_date: '',
        due_time: '',
        priority: 'Normal'
      });
      fetchAllReminders();
    } catch (error) {
      console.error('Error creating reminder:', error);
      toast.error('Failed to create reminder');
    }
  };

  // Combine and sort all reminders
  const allReminders = [...jobReminders, ...generalReminders].sort(
    (a, b) => new Date(a.followUpDate) - new Date(b.followUpDate)
  );

  if (loading) {
    return <div className="loading">Loading reminders...</div>;
  }

  return (
    <div className="reminders-container" data-testid="reminders-page">
      <Card>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <CardTitle>
                All Reminders
                <HelpTooltip 
                  content="View job reminders (linked to jobs via comment follow-ups) and general reminders (standalone tasks). Overdue reminders are highlighted in red." 
                  side="right"
                />
              </CardTitle>
              <CardDescription>Follow-up reminders for jobs and general tasks</CardDescription>
            </div>
            <Button 
              onClick={() => setShowReminderModal(true)}
              data-testid="set-reminder-btn"
              style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}
            >
              + Set Reminder
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {allReminders.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: '80px' }}>Type</TableHead>
                  <TableHead>Task / Job</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allReminders.map((reminder, idx) => (
                  <TableRow key={`${reminder.type}-${idx}`} style={{
                    backgroundColor: reminder.daysOverdue > 0 ? '#fee2e2' : reminder.daysOverdue === 0 ? '#fef3c7' : 'transparent'
                  }}>
                    <TableCell>
                      {reminder.type === 'job' ? (
                        <span style={{ 
                          background: '#dbeafe', 
                          color: '#1e40af', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          JOB
                        </span>
                      ) : (
                        <span style={{ 
                          background: '#f3e8ff', 
                          color: '#7c3aed', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          📌 TASK
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-semibold" style={{ fontSize: '1rem' }}>
                      {reminder.type === 'job' ? (
                        <>#{reminder.jobNumber} - {reminder.customerName}</>
                      ) : (
                        <>{reminder.title}</>
                      )}
                    </TableCell>
                    <TableCell style={{ fontSize: '1rem', maxWidth: '300px' }}>
                      {reminder.type === 'job' ? (
                        reminder.commentText
                      ) : (
                        <>
                          {reminder.description || '-'}
                          {reminder.priority !== 'Normal' && (
                            <span style={{ 
                              marginLeft: '0.5rem',
                              background: reminder.priority === 'High' ? '#fef2f2' : '#f0fdf4',
                              color: reminder.priority === 'High' ? '#dc2626' : '#16a34a',
                              padding: '0.125rem 0.375rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>
                              {reminder.priority}
                            </span>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell style={{ fontSize: '1rem' }}>
                      {reminder.followUpDate}
                      {reminder.type === 'general' && reminder.due_time && (
                        <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                          @ {reminder.due_time}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {reminder.daysOverdue > 0 ? (
                        <span style={{ color: '#dc2626', fontWeight: '600', fontSize: '1rem' }}>
                          {reminder.daysOverdue} day{reminder.daysOverdue > 1 ? 's' : ''} overdue
                        </span>
                      ) : reminder.daysOverdue === 0 ? (
                        <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '1rem' }}>Due Today</span>
                      ) : (
                        <span style={{ color: '#10b981', fontSize: '1rem' }}>
                          In {Math.abs(reminder.daysOverdue)} day{Math.abs(reminder.daysOverdue) > 1 ? 's' : ''}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {reminder.type === 'job' ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/job/${reminder.jobId}`)}
                              data-testid={`view-job-${reminder.jobNumber}`}
                            >
                              View Job
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleCompleteJobReminder(reminder.jobId, reminder.commentIndex)}
                              data-testid={`complete-job-reminder-${idx}`}
                            >
                              Complete
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleCompleteGeneralReminder(reminder.id)}
                              data-testid={`complete-general-reminder-${idx}`}
                            >
                              Complete
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteGeneralReminder(reminder.id)}
                              style={{ color: '#dc2626', borderColor: '#dc2626' }}
                              data-testid={`delete-general-reminder-${idx}`}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              <p>No active reminders</p>
              <Button 
                onClick={() => setShowReminderModal(true)}
                variant="outline"
                style={{ marginTop: '1rem' }}
              >
                + Create your first reminder
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Set Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Set New Reminder</DialogTitle>
            <DialogDescription>
              Create a general reminder for tasks not related to a specific job.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <div className="form-field">
              <Label htmlFor="reminder-title">Task Name *</Label>
              <Input
                id="reminder-title"
                placeholder="e.g., Call supplier about delivery"
                value={newReminder.title}
                onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                data-testid="reminder-title-input"
              />
            </div>
            
            <div className="form-field">
              <Label htmlFor="reminder-description">Description (optional)</Label>
              <Textarea
                id="reminder-description"
                placeholder="Additional details..."
                value={newReminder.description}
                onChange={(e) => setNewReminder({ ...newReminder, description: e.target.value })}
                rows={2}
                data-testid="reminder-description-input"
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className="form-field" style={{ flex: 1 }}>
                <Label htmlFor="reminder-date">Due Date *</Label>
                <Input
                  id="reminder-date"
                  type="date"
                  value={newReminder.due_date}
                  onChange={(e) => setNewReminder({ ...newReminder, due_date: e.target.value })}
                  data-testid="reminder-date-input"
                />
              </div>
              
              <div className="form-field" style={{ flex: 1 }}>
                <Label htmlFor="reminder-time">Due Time (optional)</Label>
                <Input
                  id="reminder-time"
                  type="time"
                  value={newReminder.due_time}
                  onChange={(e) => setNewReminder({ ...newReminder, due_time: e.target.value })}
                  data-testid="reminder-time-input"
                />
              </div>
            </div>
            
            <div className="form-field">
              <Label htmlFor="reminder-priority">Priority</Label>
              <Select
                value={newReminder.priority}
                onValueChange={(value) => setNewReminder({ ...newReminder, priority: value })}
              >
                <SelectTrigger id="reminder-priority" data-testid="reminder-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter style={{ marginTop: '1.5rem' }}>
            <Button variant="outline" onClick={() => setShowReminderModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateReminder}
              data-testid="create-reminder-btn"
              style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}
            >
              Create Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemindersPage;
